import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { LineChart, TrendingUp } from "lucide-react";
import "./StepThree.css";
import CryptoIconWrapper from "../cryptoicons/CryptoIconWrapper";

const StepThree = ({
  onNext,
  onBack,
  currentStep,
  isLastStep,
  selectedTokens,
  initialHandicapSettings = { marketCapWeight: 50, volatilityWeight: 50 },
}) => {
  const [marketCapWeight, setMarketCapWeight] = useState(
    initialHandicapSettings.marketCapWeight || 50
  );
  const [volatilityWeight, setVolatilityWeight] = useState(
    initialHandicapSettings.volatilityWeight || 50
  );
  const [handicaps, setHandicaps] = useState({});
  const [scrollProgress, setScrollProgress] = useState(0);

  const containerRef = useRef(null);
  const thumbRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startScrollTopRef = useRef(0);

  const calculateHandicaps = useCallback(() => {
    if (!selectedTokens?.length) return;

    const newHandicaps = {};
    const totalWeight = marketCapWeight + volatilityWeight;

    selectedTokens.forEach((token, index) => {
      // Calculate weighted handicap based on position and weights
      const position = selectedTokens.length - index;
      const marketCapFactor = (marketCapWeight / 50) * position;
      const volatilityFactor = (volatilityWeight / 50) * position;

      // Base multiplier increases with total weight
      const baseMultiplier = totalWeight / 50;

      // Calculate final ratio with minimum of 1
      const ratio = Math.max(
        Math.round((marketCapFactor + volatilityFactor) * baseMultiplier),
        1
      );

      newHandicaps[token.symbol] = ratio;
    });

    setHandicaps(newHandicaps);
  }, [selectedTokens, marketCapWeight, volatilityWeight]);

  useEffect(() => {
    if (scrollProgress) {
      console.log("Scroll progress updated:", scrollProgress);
    }
  }, [scrollProgress]);

  useEffect(() => {
    calculateHandicaps();
  }, [calculateHandicaps]);

  // Scrollbar handlers
  const handleScroll = useCallback(() => {
    if (!containerRef.current || !thumbRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
    setScrollProgress(scrollPercentage);

    // Update custom scrollbar
    const trackHeight = containerRef.current.clientHeight;
    const thumbHeight = (clientHeight / scrollHeight) * trackHeight;
    const maxScrollTop = trackHeight - thumbHeight;
    const thumbPosition = (scrollPercentage / 100) * maxScrollTop;

    thumbRef.current.style.height = `${thumbHeight}px`;
    thumbRef.current.style.transform = `translateY(${thumbPosition}px)`;
  }, []);

  const handleThumbMouseDown = (e) => {
    isDraggingRef.current = true;
    startYRef.current = e.clientY;
    startScrollTopRef.current = containerRef.current.scrollTop;
    document.addEventListener("mousemove", handleThumbDrag);
    document.addEventListener("mouseup", handleThumbMouseUp);
  };

  const handleThumbDrag = useCallback((e) => {
    if (!isDraggingRef.current || !containerRef.current) return;

    const deltaY = e.clientY - startYRef.current;
    const { scrollHeight, clientHeight } = containerRef.current;
    const trackHeight = clientHeight;
    const thumbHeight = (clientHeight / scrollHeight) * trackHeight;
    const scrollMultiplier =
      (scrollHeight - clientHeight) / (trackHeight - thumbHeight);

    containerRef.current.scrollTop =
      startScrollTopRef.current + deltaY * scrollMultiplier;
  }, []);

  const handleThumbMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    document.removeEventListener("mousemove", handleThumbDrag);
    document.removeEventListener("mouseup", handleThumbMouseUp);
  }, [handleThumbDrag]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      handleScroll(); // Initial calculation
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
      document.removeEventListener("mousemove", handleThumbDrag);
      document.removeEventListener("mouseup", handleThumbMouseUp);
    };
  }, [handleScroll, handleThumbDrag, handleThumbMouseUp]);

  const handleSliderChange = (setValue, value) => {
    setValue(value);
  };

  const generateHandicapSummary = () => {
    if (!selectedTokens?.length) return "";

    const lowestMarketCapToken = selectedTokens[selectedTokens.length - 1];
    const highestMarketCapTokens = selectedTokens
      .slice(0, 2)
      .map((t) => t.symbol)
      .filter(Boolean)
      .join(" and ");

    const handicapValue = ((marketCapWeight + volatilityWeight) / 20).toFixed(
      2
    );

    return `${lowestMarketCapToken.symbol} would need to outperform by at least ${handicapValue}% in its raw percentage change to overcome its handicap, while ${highestMarketCapTokens} receive boosts. For example, if BTC (a high market cap token) is assigned a handicap of 3:1, it means BTC's percentage change is effectively tripled to level the playing field against more volatile tokens like SOL.`;
  };

  return (
    <>
      <div className="handicapping-container" ref={containerRef}>
        <div className="wizard-header">
          <div className="step-indicator">3</div>
          <div>
            <h2 className="wizard-title">Handicapping</h2>
            <p className="wizard-description">
              Adjust how token performance is measured using market cap,
              volatility, and liquidity factors to create a fair and exciting
              race.
            </p>
          </div>
        </div>

        <div className="handicap-settings">
          <div className="handicap-section">
            <div className="section-header">
              <LineChart size={20} />
              <h3>Market Cap</h3>
            </div>
            <p className="section-description">
              Adjust how market cap influences token performance in the race.
              Larger market cap tokens, like Bitcoin, are more stable and
              typically experience smaller percentage changes, while smaller
              market cap tokens are more volatile. Use this slider to give
              larger tokens a meaningful handicap boost, making the race more
              competitive and balanced.
            </p>
            <div className="slider-container">
              <span className="slider-value">0</span>
              <div className="slider-wrapper">
                {/* Value bubble positioned above the thumb */}
                <div
                  className="value-bubble"
                  style={{ left: `${marketCapWeight}%` }}
                >
                  {marketCapWeight}
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={marketCapWeight}
                  onChange={(e) =>
                    handleSliderChange(
                      setMarketCapWeight,
                      parseInt(e.target.value)
                    )
                  }
                  className="handicap-slider"
                  style={{ "--slider-percentage": `${marketCapWeight}%` }}
                />
              </div>
              <span className="slider-value">100</span>
            </div>
          </div>

          <div className="handicap-section">
            <div className="section-header">
              <TrendingUp size={20} />
              <h3>Volatility</h3>
            </div>
            <p className="section-description">
              Adjust how volatility influences token performance in the race.
              Highly volatile tokens can experience dramatic percentage changes,
              making them more unpredictable, while stable tokens tend to have
              steadier movements. Use this slider to give stable tokens a boost,
              leveling the field against more volatile competitors.
            </p>
            <div className="slider-container">
              <span className="slider-value">0</span>
              <div className="slider-wrapper">
                <div
                  className="value-bubble"
                  style={{ left: `${volatilityWeight}%` }}
                >
                  {volatilityWeight}
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volatilityWeight}
                  onChange={(e) =>
                    handleSliderChange(
                      setVolatilityWeight,
                      parseInt(e.target.value)
                    )
                  }
                  className="handicap-slider"
                  style={{ "--slider-percentage": `${volatilityWeight}%` }}
                />
              </div>
              <span className="slider-value">100</span>
            </div>
          </div>

          <div className="post-handicap">
            <h3>Post Handicap</h3>
            <p className="handicap-summary">{generateHandicapSummary()}</p>
            <p className="handicap-explanation">
              <strong>Why Handicap?</strong> Large market cap tokens like BTC
              tend to have smaller percentage changes. By assigning a handicap,
              their performance is effectively multiplied (for example, a 3:1
              handicap means a 1% change counts as 3%), helping to level the
              playing field against more volatile tokens like SOL.
            </p>

            <div className="tokens-comparison">
              {selectedTokens?.map((token, index) => (
                <React.Fragment key={token.symbol}>
                  <div className="token-handicap">
                    <div className="token-icon">
                      <CryptoIconWrapper name={token.symbol.toLowerCase()} size={40} />
                    </div>
                    <div
                      className="handicap-ratio"
                      title={`Handicap Ratio: ${handicaps[token.symbol] || index + 1
                        }:1.
This means that the token's raw performance percentage is multiplied by this ratio in the race.`}
                    >
                      {`${handicaps[token.symbol] || index + 1}:1`}
                    </div>
                  </div>
                  {index < selectedTokens.length - 1 && (
                    <div className="vs-separator">VS</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="button-container">
          {currentStep > 1 && (
            <button onClick={onBack} className="back-button">
              Back
            </button>
          )}
          <button
            onClick={() =>
              onNext({
                handicapSettings: {
                  marketCapWeight,
                  volatilityWeight,
                },
              })
            }
            className="continue-button"
            disabled={isLastStep}
          >
            {isLastStep ? "Finish" : "Continue"}
          </button>
        </div>

        {/* Custom Scrollbar */}
        <div className="custom-scrollbar">
          <div className="scrollbar-track">
            <div
              ref={thumbRef}
              className="scrollbar-thumb"
              onMouseDown={handleThumbMouseDown}
            />
          </div>
        </div>
      </div>
    </>
  );
};

StepThree.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  isLastStep: PropTypes.bool.isRequired,
  selectedTokens: PropTypes.arrayOf(
    PropTypes.shape({
      symbol: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  initialHandicapSettings: PropTypes.shape({
    marketCapWeight: PropTypes.number,
    volatilityWeight: PropTypes.number,
  }),
};

export default StepThree;
